/**
 * <pdp-lightbox> — image lightbox custom element
 *
 * Brief 01.5 — Mekar Malaysia (2026-04-19)
 *
 * Wraps the product-media-gallery. Any click on an image-type tile inside
 * opens a native <dialog> showing the full-size image. Arrow keys + swipe
 * cycle, backdrop/close-button/Escape close.
 *
 * Non-image media (video / 3D model) are ignored — they keep Dawn's
 * inline behaviour. The lightbox only indexes <img> tiles with
 * data-pdp-lightbox-image.
 */
(function () {
  'use strict';

  if (customElements.get('pdp-lightbox')) return;

  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class PdpLightbox extends HTMLElement {
    constructor() {
      super();
      this.images = [];
      this.current = 0;
      this.dialog = null;
      this.imgEl = null;
      this.counterEl = null;
      this.thumbsEl = null;
      this.preloadEl = null;
      this._onKeydown = this._onKeydown.bind(this);
      this._onBackdrop = this._onBackdrop.bind(this);
      this._onTouchStart = this._onTouchStart.bind(this);
      this._onTouchMove = this._onTouchMove.bind(this);
      this._onTouchEnd = this._onTouchEnd.bind(this);
      this._onOpenerClick = this._onOpenerClick.bind(this);
    }

    connectedCallback() {
      this._collectImages();
      if (!this.images.length) return;
      this._buildDialog();
      this._wireOpeners();
    }

    disconnectedCallback() {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
      }
    }

    // --------------------------------------------------------------------
    // Setup
    // --------------------------------------------------------------------
    _collectImages() {
      const items = this.querySelectorAll('.product__media-item[data-media-type="image"]');
      items.forEach((item, idx) => {
        const img = item.querySelector('.product__media img');
        if (!img) return;
        const src = img.getAttribute('data-full-src') || this._biggestSrc(img);
        const alt = img.getAttribute('alt') || '';
        const thumbSrc = img.currentSrc || img.src;
        this.images.push({ src, alt, thumbSrc, item, index: idx });
        item.dataset.pdpLightboxIndex = String(this.images.length - 1);
      });
    }

    _biggestSrc(img) {
      // Prefer srcset largest descriptor; fall back to src.
      const srcset = img.getAttribute('srcset') || '';
      if (srcset) {
        const entries = srcset
          .split(',')
          .map((s) => s.trim())
          .map((s) => {
            const parts = s.split(/\s+/);
            const url = parts[0];
            const w = parseInt(parts[1] || '0', 10) || 0;
            return { url, w };
          })
          .sort((a, b) => b.w - a.w);
        if (entries.length) return entries[0].url;
      }
      return img.src;
    }

    _buildDialog() {
      const t = (key, fallback) => {
        const data = this.dataset;
        return data[key] || fallback;
      };
      const labelClose = t('labelClose', 'Close');
      const labelPrev = t('labelPrev', 'Previous image');
      const labelNext = t('labelNext', 'Next image');
      const labelThumbs = t('labelThumbs', 'Select image');

      const dialog = document.createElement('dialog');
      dialog.className = 'pdp-lightbox__overlay';
      dialog.setAttribute('aria-label', t('labelDialog', 'Product image viewer'));
      dialog.innerHTML = `
        <div class="pdp-lightbox__counter" aria-live="polite">
          <span class="pdp-lightbox__counter-current">1</span>
          <span> / </span>
          <span class="pdp-lightbox__counter-total">${this.images.length}</span>
        </div>
        <button type="button" class="pdp-lightbox__button pdp-lightbox__close" aria-label="${escapeAttr(labelClose)}">
          <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
        <button type="button" class="pdp-lightbox__button pdp-lightbox__prev" aria-label="${escapeAttr(labelPrev)}">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M11 3L5 9l6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
        </button>
        <button type="button" class="pdp-lightbox__button pdp-lightbox__next" aria-label="${escapeAttr(labelNext)}">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" focusable="false"><path d="M7 3l6 6-6 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>
        </button>
        <div class="pdp-lightbox__stage">
          <img class="pdp-lightbox__image" alt="" decoding="async">
        </div>
        <div class="pdp-lightbox__swipe" aria-hidden="true"></div>
        <ol class="pdp-lightbox__thumbs" aria-label="${escapeAttr(labelThumbs)}"></ol>
      `;

      document.body.appendChild(dialog);
      this.dialog = dialog;
      this.imgEl = dialog.querySelector('.pdp-lightbox__image');
      this.counterEl = dialog.querySelector('.pdp-lightbox__counter-current');
      this.thumbsEl = dialog.querySelector('.pdp-lightbox__thumbs');
      this.swipeEl = dialog.querySelector('.pdp-lightbox__swipe');

      // Preload slot for next/prev (lives in <head>).
      this.preloadEl = document.createElement('link');
      this.preloadEl.rel = 'preload';
      this.preloadEl.as = 'image';

      // Build thumbs.
      this.images.forEach((img, i) => {
        const li = document.createElement('li');
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pdp-lightbox__thumb';
        btn.setAttribute('aria-label', `${labelThumbs} ${i + 1}`);
        btn.dataset.index = String(i);
        const im = document.createElement('img');
        im.src = img.thumbSrc;
        im.alt = '';
        im.loading = 'lazy';
        btn.appendChild(im);
        li.appendChild(btn);
        this.thumbsEl.appendChild(li);
      });

      // Button wiring.
      dialog.querySelector('.pdp-lightbox__close').addEventListener('click', () => this.close());
      dialog.querySelector('.pdp-lightbox__prev').addEventListener('click', () => this.prev());
      dialog.querySelector('.pdp-lightbox__next').addEventListener('click', () => this.next());
      this.thumbsEl.addEventListener('click', (e) => {
        const btn = e.target.closest('button.pdp-lightbox__thumb');
        if (!btn) return;
        this.show(parseInt(btn.dataset.index, 10));
      });

      // Swipe support on mobile.
      this.swipeEl.addEventListener('touchstart', this._onTouchStart, { passive: true });
      this.swipeEl.addEventListener('touchmove', this._onTouchMove, { passive: true });
      this.swipeEl.addEventListener('touchend', this._onTouchEnd);

      // Backdrop click to close (dialog receives clicks on backdrop area).
      dialog.addEventListener('click', this._onBackdrop);
    }

    _wireOpeners() {
      // Delegated click handler — capture clicks on image tiles, suppress
      // Dawn's built-in modal-opener (which opens a different product-modal).
      this.addEventListener(
        'click',
        (e) => {
          const item = e.target.closest('.product__media-item[data-media-type="image"]');
          if (!item) return;
          // Ignore clicks on any explicit XR / deferred-media buttons.
          if (e.target.closest('.product__xr-button, .deferred-media__poster')) return;
          const idx = parseInt(item.dataset.pdpLightboxIndex, 10);
          if (Number.isNaN(idx)) return;
          e.preventDefault();
          e.stopPropagation();
          this.open(idx);
        },
        true // capture — run before Dawn's modal-opener
      );
    }

    // --------------------------------------------------------------------
    // Public API
    // --------------------------------------------------------------------
    open(index) {
      if (!this.dialog) return;
      this.current = Math.max(0, Math.min(index || 0, this.images.length - 1));
      this.show(this.current);
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.addEventListener('keydown', this._onKeydown);
      // Move focus to close button for a11y.
      const closeBtn = this.dialog.querySelector('.pdp-lightbox__close');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.removeEventListener('keydown', this._onKeydown);
    }

    next() {
      if (this.current >= this.images.length - 1) return;
      this.show(this.current + 1);
    }

    prev() {
      if (this.current <= 0) return;
      this.show(this.current - 1);
    }

    show(index) {
      if (index < 0 || index >= this.images.length) return;
      this.current = index;
      const img = this.images[index];
      if (!img) return;

      // Swap image with fade-in.
      this.imgEl.classList.remove('is-ready');
      const applyReady = () => {
        if (REDUCED_MOTION) {
          this.imgEl.classList.add('is-ready');
        } else {
          requestAnimationFrame(() => this.imgEl.classList.add('is-ready'));
        }
      };
      if (this.imgEl.src === img.src) {
        applyReady();
      } else {
        this.imgEl.onload = applyReady;
        this.imgEl.onerror = applyReady;
        this.imgEl.src = img.src;
        this.imgEl.alt = img.alt;
      }

      // Counter.
      if (this.counterEl) this.counterEl.textContent = String(index + 1);

      // Thumb state.
      this.thumbsEl.querySelectorAll('button.pdp-lightbox__thumb').forEach((btn) => {
        btn.setAttribute('aria-current', btn.dataset.index === String(index) ? 'true' : 'false');
      });

      // Nav button disabled states.
      this.dialog.querySelector('.pdp-lightbox__prev').disabled = index === 0;
      this.dialog.querySelector('.pdp-lightbox__next').disabled = index === this.images.length - 1;

      // Preload next/prev.
      this._preload(index + 1);
      this._preload(index - 1);

      // Scroll the active thumb into view.
      const activeThumb = this.thumbsEl.querySelector(`button[data-index="${index}"]`);
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
      }
    }

    _preload(index) {
      if (index < 0 || index >= this.images.length) return;
      const img = this.images[index];
      // Cheap preload: create an <img> and let the browser cache it.
      const pre = new Image();
      pre.src = img.src;
    }

    // --------------------------------------------------------------------
    // Event handlers
    // --------------------------------------------------------------------
    _onKeydown(e) {
      if (!this.dialog || !this.dialog.open) return;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.next();
          break;
        case 'Escape':
          // <dialog> handles Escape natively but we remove our listener.
          document.removeEventListener('keydown', this._onKeydown);
          break;
      }
    }

    _onBackdrop(e) {
      if (e.target === this.dialog) {
        // User clicked on the dialog itself (the backdrop area outside the image + buttons).
        this.close();
      }
    }

    _onTouchStart(e) {
      if (!e.touches || !e.touches[0]) return;
      this._touchStartX = e.touches[0].clientX;
      this._touchStartY = e.touches[0].clientY;
      this._touchDx = 0;
    }

    _onTouchMove(e) {
      if (!e.touches || !e.touches[0] || this._touchStartX == null) return;
      this._touchDx = e.touches[0].clientX - this._touchStartX;
    }

    _onTouchEnd() {
      if (this._touchStartX == null) return;
      const threshold = 40;
      if (Math.abs(this._touchDx) > threshold) {
        if (this._touchDx < 0) this.next();
        else this.prev();
      }
      this._touchStartX = null;
      this._touchStartY = null;
      this._touchDx = 0;
    }
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }

  customElements.define('pdp-lightbox', PdpLightbox);
})();
